import { Component, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivitiesService } from '@shared/services/activities.service';
import { WbsColourService } from '@shared/services/wbs-colour.service';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserSettingsService } from '@shared/services/user-settings.service';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ETaskKeys, ETaskOptionalKeys } from '@static/models/task';
import { StorageService } from '@static/services/storage.service';
import { AuthService } from '@static/services/auth.service';

interface IOptionalKeys {
  key: string;
  name: string;
  selected: boolean;
}

@Component({
  selector: 'app-user-settings',
  imports: [
    MatButtonModule,
    MatDialogModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    FormsModule,
    MatSlideToggleModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './user-settings.component.html',
  styleUrl: './user-settings.component.scss',
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } }
  ]
})
export class UserSettingsComponent {
  private _storageService: StorageService = inject(StorageService);
  public dialogRef: MatDialogRef<UserSettingsComponent> = inject(MatDialogRef<UserSettingsComponent>);
  public data: string = inject(MAT_DIALOG_DATA);
  private _userSettingsService: UserSettingsService = inject(UserSettingsService);
  private _activitiesService: ActivitiesService = inject(ActivitiesService);
  private _wbsColourService: WbsColourService = inject(WbsColourService);
  private _authService: AuthService = inject(AuthService);

  public wbsLevelColours: string[];
  public wbsTextColours: string[];

  public optionalKeys: IOptionalKeys[];

  constructor() {
    this.wbsLevelColours = [...this._wbsColourService.levelMap];
    this.wbsTextColours = [...this._wbsColourService.textMap];

    this.optionalKeys = [];
    Object.keys(ETaskOptionalKeys).forEach((key) => {
      this.optionalKeys.push({
        key,
        name: ETaskKeys[key as keyof typeof ETaskKeys],
        selected: (this._userSettingsService.filterForm.get('optionalFields')?.value as string)?.includes(key) ?? false
      })
    });
  }

  /**
   * Get the filter form
   */
  public get filterForm(): FormGroup {
    return this._userSettingsService.filterForm;
  }

  /**
   * Check if pagination is enabled
   */
  public get paginationEnabled() : boolean {
    return this._userSettingsService.filterForm.get('pagination')?.value ?? false;
  }

  /**
   * Update the optional fields and save the user settings
   *
   * @param field field to update
   * @param state field selected state
   */
  public updateOptionalSelected(field: IOptionalKeys, state: boolean): void {
    // do things
    const control = this._userSettingsService.filterForm.get('optionalFields') as FormControl;
    let value = control.value as string;
    const exists = value.includes(field.key);
    if (state) {
      if (!exists) value += `${value.length > 0 ? ',' : ''}${field.key}`;
    } else {
      if (exists) value = value.replace(`,${field.key}`, '').replace(field.key, '');
    }
    control.setValue(value);
    field.selected = state;
    this._userSettingsService.updateFilterValue('optionalFields');
  }

  /**
   * Pagination slider toggle
   */
  public sliderChange(): void {
    const paginationCtrl = this._userSettingsService.filterForm.get('pagination');
    const pageSizeCtrl = this._userSettingsService.filterForm.get('pageSize');
    if (paginationCtrl && pageSizeCtrl) {
      if (paginationCtrl.value) pageSizeCtrl.enable();
      else pageSizeCtrl.disable();
    }
    this.requestUpdate();
  }

  /**
   * Request update of WBS structure with pagination changes
   */
  public requestUpdate(): void {
    this._userSettingsService.updateFilterValue('pagination');
    this._userSettingsService.updateFilterValue('pageSize');
    this._activitiesService.getWBSTaskStructure();
  }

  /**
   * Update the WBS colours
   */
  public colourChange(): void {
    this._wbsColourService.levelMap = [...this.wbsLevelColours];
    this._wbsColourService.textMap = [...this.wbsTextColours];
    if (this._storageService.hasChanges) this._storageService.commitChanges(this._authService.checkToken());
  }

  /**
   * Reset the colours to defaults
   */
  public resetColours(): void {
    this._wbsColourService.resetColours();
    this.wbsLevelColours = [...this._wbsColourService.levelMap];
    this.wbsTextColours = [...this._wbsColourService.textMap];
    if (this._storageService.hasChanges) this._storageService.commitChanges(this._authService.checkToken());
  }

  /**
   * Clear the local cache
   */
  public clearCache(): void {
    this._userSettingsService.clearCache();
  }

  /**
   * Close the dialog
   */
  public close(): void {
    this.dialogRef.close();
  }
}
